/**
 * ProjectVarianceTab — surfaces where task effort is drifting from estimate.
 *
 * Four sections:
 *  1. Summary tiles — Est vs Actual (manual + timesheet) at project level.
 *  2. Over-run tasks — total_effort > est_hours × 1.2 (default threshold, adjustable).
 *  3. Under-utilised — est_hours ≥ 4h but total_effort < 25% of est.
 *  4. Missing timesheets — manual_effort ≥ 1h but zero timesheet_hours (potential missing time logs).
 *  5. Weekly heatmap — hours logged per task per week (last 8 weeks).
 *
 * Everything is client-side computed from GET /workspaces/{ws}/tasks-search?project_id=...
 * plus GET /workspaces/{ws}/time-entries?project_id=... (for the heatmap).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { AlertTriangle, TrendingDown, Clock, BarChart3, Timer } from "lucide-react";
import EmptyState from "@/components/EmptyState";

const OVERRUN_MULTIPLIER = 1.2;

export default function ProjectVarianceTab({ project }) {
  const [tasks, setTasks] = useState(null);
  const [entries, setEntries] = useState([]);
  const [overThreshold, setOverThreshold] = useState(OVERRUN_MULTIPLIER);

  useEffect(() => {
    if (!project) return;
    (async () => {
      try {
        const [t, te] = await Promise.all([
          api.get(`/workspaces/${project.workspace_id}/tasks-search`, { params: { project_id: project.id } }),
          api.get(`/workspaces/${project.workspace_id}/time-entries/for-project/${project.id}`, { params: { days: 60 } }).catch(() => ({ data: [] })),
        ]);
        setTasks(t.data || []);
        setEntries(te.data || []);
      } catch {
        setTasks([]);
      }
    })();
  }, [project?.id]);

  const stats = useMemo(() => {
    const list = tasks || [];
    const overRuns = list.filter((t) => (t.est_hours || 0) > 0 && (t.total_effort_hours || 0) > (t.est_hours * overThreshold));
    const underUtil = list.filter((t) => (t.est_hours || 0) >= 4 && (t.total_effort_hours || 0) < ((t.est_hours || 0) * 0.25) && t.status !== "done");
    const missingTs = list.filter((t) => (t.manual_effort_hours || 0) >= 1 && (t.timesheet_hours || 0) === 0);
    const totalEst = list.reduce((s, t) => s + (t.est_hours || 0), 0);
    const totalActual = list.reduce((s, t) => s + (t.total_effort_hours || 0), 0);
    const totalManual = list.reduce((s, t) => s + (t.manual_effort_hours || 0), 0);
    const totalTs = list.reduce((s, t) => s + (t.timesheet_hours || 0), 0);
    return { list, overRuns, underUtil, missingTs, totalEst, totalActual, totalManual, totalTs };
  }, [tasks, overThreshold]);

  if (tasks === null) return <div className="text-sm text-muted-foreground p-6">Loading variance report…</div>;

  const projectVarianceHours = round1((stats.totalActual + Number(project?.manual_effort_hours || 0)) - stats.totalEst);
  const projectVariancePct = stats.totalEst ? Math.round(((stats.totalActual + Number(project?.manual_effort_hours || 0)) / stats.totalEst - 1) * 100) : null;

  return (
    <div className="space-y-6" data-testid="project-variance-tab">
      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Total estimated" value={`${round1(stats.totalEst)}h`} icon={Timer} testid="pv-est" />
        <Tile label="Actual (manual + timesheet)" value={`${round1(stats.totalActual)}h`} icon={BarChart3} testid="pv-actual"
          sub={`${round1(stats.totalManual)}h manual · ${round1(stats.totalTs)}h logged`} />
        <Tile label="Project-level manual" value={`${round1(project?.manual_effort_hours || 0)}h`} testid="pv-project-manual"
          sub={project?.manual_effort_note ? `"${project.manual_effort_note.slice(0, 40)}${project.manual_effort_note.length > 40 ? "…" : ""}"` : "no note"} />
        <Tile
          label="Variance vs estimate"
          value={`${projectVarianceHours >= 0 ? "+" : ""}${projectVarianceHours}h${projectVariancePct !== null ? ` (${projectVariancePct >= 0 ? "+" : ""}${projectVariancePct}%)` : ""}`}
          tone={projectVarianceHours > 0 ? "text-rose-600" : "text-emerald-600"}
          testid="pv-variance"
        />
      </div>

      {/* Over-run tasks */}
      <Panel
        title="Over-run tasks"
        icon={TrendingDown}
        tone="border-rose-500/40 bg-rose-500/5"
        subtitle={
          <span className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
            Total effort &gt;
            <Input
              type="number"
              step="0.1"
              min="1"
              value={overThreshold}
              onChange={(e) => setOverThreshold(Math.max(1.01, Number(e.target.value) || 1.2))}
              className="h-6 w-14 text-xs rounded-none"
              data-testid="pv-overrun-threshold"
            />
            × estimate
          </span>
        }
        testid="pv-overrun-panel"
      >
        {stats.overRuns.length === 0 ? (
          <EmptyState title="No over-runs" description="Every task is at or below its estimate. Nice." icon={Timer} testid="pv-overrun-empty" />
        ) : (
          <TasksTable tasks={stats.overRuns} extraCol={(t) => {
            const pct = Math.round(((t.total_effort_hours / t.est_hours) - 1) * 100);
            return <span className="font-mono text-xs text-rose-700 font-semibold" title="% over estimate">+{pct}%</span>;
          }} extraLabel="Over" />
        )}
      </Panel>

      {/* Under-utilised */}
      <Panel
        title="Under-utilised tasks"
        icon={AlertTriangle}
        tone="border-amber-500/40 bg-amber-500/5"
        subtitle={<span className="text-[11px] text-muted-foreground">Estimated ≥ 4h but less than 25% consumed and still open — likely stalled or over-scoped.</span>}
        testid="pv-underutil-panel"
      >
        {stats.underUtil.length === 0 ? (
          <EmptyState title="Nothing stalled" description="Every large task is progressing." icon={Timer} testid="pv-underutil-empty" />
        ) : (
          <TasksTable tasks={stats.underUtil} extraCol={(t) => {
            const pct = Math.round(((t.total_effort_hours || 0) / t.est_hours) * 100);
            return <span className="font-mono text-xs text-amber-700">{pct}% used</span>;
          }} extraLabel="Used" />
        )}
      </Panel>

      {/* Missing timesheets */}
      <Panel
        title="Missing timesheets"
        icon={Clock}
        tone="border-primary/40 bg-primary/5"
        subtitle={<span className="text-[11px] text-muted-foreground">Task has a large <b>manual effort</b> value but no timesheet activity — worth confirming the assignee logged their time.</span>}
        testid="pv-missing-panel"
      >
        {stats.missingTs.length === 0 ? (
          <EmptyState title="Clean" description="All manual entries have timesheet activity backing them." icon={Timer} testid="pv-missing-empty" />
        ) : (
          <TasksTable tasks={stats.missingTs} extraCol={(t) => (
            <span className="font-mono text-xs" title={t.manual_effort_note || ""}>
              {t.manual_effort_hours}h manual{t.manual_effort_note ? " •" : ""}
            </span>
          )} extraLabel="Manual only" />
        )}
      </Panel>

      {/* Weekly heatmap */}
      <Panel title="Timesheet activity — last 8 weeks" icon={BarChart3} tone="border-border" testid="pv-heatmap-panel">
        <WeeklyHeatmap tasks={stats.list} entries={entries} />
      </Panel>
    </div>
  );
}

function round1(n) { return Math.round((Number(n) || 0) * 10) / 10; }

function Tile({ label, value, sub, tone = "", icon: Icon, testid }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && <Icon className="w-3 h-3 text-muted-foreground" />}
      </div>
      <div className={`text-lg font-bold font-mono ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function Panel({ title, icon: Icon, tone = "", subtitle, testid, children }) {
  return (
    <div className={`border ${tone}`} data-testid={testid}>
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 flex-wrap">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <div className="label-eyebrow text-[10px]">{title}</div>
        {subtitle && <div className="ml-auto">{subtitle}</div>}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function TasksTable({ tasks, extraCol, extraLabel }) {
  return (
    <div className="border border-border">
      <div className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-border label-eyebrow text-[10px] bg-muted/40">
        <div className="col-span-6">Task</div>
        <div className="col-span-2 text-right">Est (h)</div>
        <div className="col-span-2 text-right">Actual (h)</div>
        <div className="col-span-2 text-right">{extraLabel}</div>
      </div>
      {tasks.map((t) => (
        <div key={t.id} className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-border last:border-0 items-center text-xs hover:bg-muted/20" data-testid={`pv-row-${t.id}`}>
          <div className="col-span-6 min-w-0">
            <Link to={`/app/tasks?tid=${t.id}`} className="hover:text-primary truncate block" title={t.title}>{t.title}</Link>
            {t.assignee_name && <div className="text-[10px] text-muted-foreground">{t.assignee_name}</div>}
          </div>
          <div className="col-span-2 text-right font-mono">{round1(t.est_hours || 0)}</div>
          <div className="col-span-2 text-right font-mono font-semibold">{round1(t.total_effort_hours || 0)}</div>
          <div className="col-span-2 text-right">{extraCol(t)}</div>
        </div>
      ))}
    </div>
  );
}

function WeeklyHeatmap({ tasks, entries }) {
  // Build last 8 ISO-weeks. Sum hours by (task_id, week_start).
  const weeks = useMemo(() => {
    const out = [];
    const now = new Date();
    const day = now.getUTCDay(); // 0=sun..6=sat
    // Move back to previous Monday
    const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - ((day + 6) % 7)));
    for (let i = 7; i >= 0; i--) {
      const start = new Date(monday);
      start.setUTCDate(monday.getUTCDate() - i * 7);
      out.push({ start, key: start.toISOString().slice(0, 10) });
    }
    return out;
  }, []);

  const byTaskWeek = useMemo(() => {
    const m = {};
    (entries || []).forEach((e) => {
      const d = new Date(e.date || e.entry_date || e.created_at);
      if (isNaN(d)) return;
      const day = d.getUTCDay();
      const mon = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - ((day + 6) % 7)));
      const wk = mon.toISOString().slice(0, 10);
      const tid = e.task_id || "_no_task";
      m[tid] = m[tid] || {};
      m[tid][wk] = (m[tid][wk] || 0) + (Number(e.hours) || 0);
    });
    return m;
  }, [entries]);

  // Only show tasks that have any entries; if none, show a small hint.
  const taskIds = Object.keys(byTaskWeek);
  const taskById = Object.fromEntries((tasks || []).map((t) => [t.id, t]));
  const rows = taskIds
    .map((tid) => ({ id: tid, title: (taskById[tid] || {}).title || (tid === "_no_task" ? "(no task)" : tid), data: byTaskWeek[tid] }))
    .sort((a, b) => Object.values(b.data).reduce((s, v) => s + v, 0) - Object.values(a.data).reduce((s, v) => s + v, 0))
    .slice(0, 15);

  if (rows.length === 0) {
    return <div className="text-xs text-muted-foreground p-4 text-center">No timesheet entries yet for this project. Log some hours to see the heatmap.</div>;
  }
  // Compute max for color scaling
  const max = Math.max(1, ...rows.flatMap((r) => Object.values(r.data)));

  return (
    <div className="border border-border overflow-x-auto" data-testid="pv-heatmap">
      <table className="w-full text-xs">
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left px-2 py-1.5 border-r border-border">Task</th>
            {weeks.map((w) => (
              <th key={w.key} className="text-center px-1 py-1.5 border-r border-border last:border-0 text-[9px] font-normal text-muted-foreground min-w-[36px]">
                {w.start.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </th>
            ))}
            <th className="text-right px-2 py-1.5 min-w-[50px]">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const total = Object.values(r.data).reduce((s, v) => s + v, 0);
            return (
              <tr key={r.id} className="border-t border-border">
                <td className="px-2 py-1 border-r border-border truncate max-w-[220px]" title={r.title}>{r.title}</td>
                {weeks.map((w) => {
                  const h = r.data[w.key] || 0;
                  const opacity = h ? 0.15 + 0.85 * (h / max) : 0;
                  return (
                    <td key={w.key} className="text-center border-r border-border last:border-0 relative">
                      <div className="h-6 flex items-center justify-center" style={{ backgroundColor: h ? `rgba(59, 130, 246, ${opacity})` : "transparent" }} title={`${round1(h)}h · week of ${w.key}`}>
                        {h ? <span className="text-[10px] font-mono">{round1(h)}</span> : <span className="text-muted-foreground/40">·</span>}
                      </div>
                    </td>
                  );
                })}
                <td className="px-2 py-1 text-right font-mono font-semibold">{round1(total)}h</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
